import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { ChevronRight, Folder, FolderOpen, Plus, MoreHorizontal, Archive, Sparkles, Lock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Board } from '@/lib/api/types';
import { useAddAssetsToBoard, useBulkAction, useBoardChildrenR1, useLibraryBoardsR1 } from '@/lib/api/queries';
import { keys } from '@/lib/api/keys';
import { hasAssetDrag, readAssetDragData } from '@/features/grid/dnd';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { usePermission } from './usePermission';
import { cx } from '@/lib/format';
import { writeLS } from '@/stores/ui';
// --- release1/B ---
import { Menu } from '@/components/Menu';
import { useTidyPrefs } from '@/stores/gridPrefs';
import { useBoardTidyMenuItems } from '@/features/pins/BoardTidyActions';
// --- release3/D ---
import { isPrivateBoard } from '@/features/board-guests/visibility';

type TreeCtx = {
  ws: string;
  wsId: string;
  expanded: Set<string>;
  toggle: (id: string) => void;
  onCreate: (parent: Board) => void;
  onDropAssets: (target: Board, payload: { ids: string[]; source_board_id: string | null }, copy: boolean) => void;
};

function useExpanded(wsId: string) {
  const key = `expandedBoards:${wsId}`;
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      return new Set<string>(JSON.parse(localStorage.getItem(key) ?? '[]'));
    } catch {
      return new Set();
    }
  });
  const toggle = useCallback(
    (id: string) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        writeLS(key, [...next]);
        return next;
      }),
    [key],
  );
  return { expanded, toggle, setExpanded };
}

export function BoardTree({ libraryId, onCreate }: { libraryId: string; onCreate: (parent: Board | null, libraryId: string) => void }) {
  const { ws = '' } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const showArchived = useTidyPrefs((s) => s.showArchived); // release1/B
  const roots = useLibraryBoardsR1(libraryId, showArchived);
  const { expanded, toggle, setExpanded } = useExpanded(wsId);
  const { boardId } = useParams();
  const qc = useQueryClient();
  const addAssets = useAddAssetsToBoard();
  const bulk = useBulkAction(wsId);

  // auto-expand ancestors of the current board
  useEffect(() => {
    if (!boardId) return;
    const b = qc.getQueryData<Board>(keys.board(boardId));
    if (!b?.breadcrumbs) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const c of b.breadcrumbs ?? []) if (c.id !== boardId) next.add(c.id);
      return next.size === prev.size ? prev : next;
    });
  }, [boardId, qc, setExpanded]);

  const ctx = useMemo<TreeCtx>(
    () => ({
      ws,
      wsId,
      expanded,
      toggle,
      onCreate: (parent) => onCreate(parent, libraryId),
      onDropAssets: (target, payload, copy) => {
        if (payload.source_board_id === target.id) return;
        if (copy || !payload.source_board_id) {
          addAssets.mutate({ boardId: target.id, assetIds: payload.ids, fromBoardId: payload.source_board_id, mode: 'copy' }, { onSuccess: () => toast({ kind: 'success', title: `Copied ${payload.ids.length} to ${target.name}` }) });
        } else {
          bulk.mutate({ asset_ids: payload.ids, action: 'move_to_board', params: { board_id: target.id, from_board_id: payload.source_board_id } }, { onSuccess: () => toast({ kind: 'success', title: `Moved ${payload.ids.length} to ${target.name}` }) });
        }
      },
    }),
    [ws, wsId, expanded, toggle, onCreate, libraryId, addAssets, bulk],
  );

  if (roots.isPending) return <div className="px-2 py-1 text-xs text-fg-subtle">Loading…</div>;
  if (roots.isError) return <div className="px-2 py-1 text-xs text-danger">Failed to load boards</div>;
  return (
    <ul role="tree" aria-label="Boards" className="space-y-px">
      {roots.data.map((b) => (
        <TreeNode key={b.id} board={b} depth={0} ctx={ctx} />
      ))}
    </ul>
  );
}

function TreeNode({ board, depth, ctx }: { board: Board; depth: number; ctx: TreeCtx }) {
  const open = ctx.expanded.has(board.id);
  const hasChildren = board.child_board_count > 0;
  const showArchived = useTidyPrefs((s) => s.showArchived); // release1/B
  const children = useBoardChildrenR1(open && hasChildren ? board.id : undefined, showArchived);
  const archived = !!board.archived_at;
  const [menu, setMenu] = useState(false);
  const menuBtn = useRef<HTMLButtonElement>(null);
  const tidyItems = useBoardTidyMenuItems(board);
  const [over, setOver] = useState(false);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  const smart = board.kind === 'smart'; // release2/A: a live query holds no assets and no child boards

  const onDragOver = (e: DragEvent) => {
    if (smart || !hasAssetDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
    if (!over) setOver(true);
  };
  const onDrop = (e: DragEvent) => {
    setOver(false);
    if (smart) return;
    const p = readAssetDragData(e);
    if (!p) return;
    e.preventDefault();
    ctx.onDropAssets(board, p, e.altKey || e.dataTransfer.dropEffect === 'copy');
  };

  return (
    <li role="treeitem" aria-expanded={hasChildren ? open : undefined} aria-level={depth + 1}>
      <div
        className={cx('group flex items-center gap-1 rounded-md pr-1 text-sm', over && 'bg-accent-soft ring-1 ring-accent', archived && 'opacity-70')}
        data-archived={archived || undefined}
        data-smart={smart || undefined}
        style={{ paddingLeft: 4 + depth * 14 }}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); }} // L13: crossing into a child is not a leave
        onDrop={onDrop}
      >
        <button
          type="button"
          aria-label={open ? 'Collapse' : 'Expand'}
          tabIndex={-1}
          className={cx('flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-sunken', !hasChildren && 'invisible')}
          onClick={() => ctx.toggle(board.id)}
        >
          <ChevronRight size={13} className={cx('transition-transform', open && 'rotate-90')} />
        </button>
        <NavLink
          to={`/w/${ctx.ws}/b/${board.id}`}
          className={({ isActive }) => cx('flex min-w-0 flex-1 items-center gap-1.5 rounded-md py-1 pr-1 hover:bg-bg-sunken', isActive && 'bg-bg-sunken font-medium text-accent')}
          style={board.color ? { ['--board' as string]: board.color } : undefined}
        >
          {smart ? <Sparkles size={14} className="shrink-0 text-accent" style={{ color: board.color ?? undefined }} aria-label="Smart board" /> : open ? <FolderOpen size={14} className="shrink-0" style={{ color: board.color ?? undefined }} /> : <Folder size={14} className="shrink-0" style={{ color: board.color ?? undefined }} />}
          <span className={cx('truncate', archived && 'text-fg-subtle line-through decoration-fg-subtle')}>{board.name}</span>
          {archived && <Archive size={11} className="shrink-0 text-fg-subtle" aria-label="Archived" />}
          {isPrivateBoard(board) && <Lock size={11} className="shrink-0 text-fg-subtle" aria-label="Private board" data-private-board />} {/* release3/D */}
          <span className="ml-auto shrink-0 text-xs text-fg-subtle tabular-nums">{smart ? (board.smart_count ?? '') : board.asset_count_deep}</span>
        </NavLink>
        {tidyItems.length > 0 && (
          <>
            <button ref={menuBtn} type="button" aria-label={`More actions for ${board.name}`} aria-haspopup="menu" aria-expanded={menu} className={cx('h-5 w-5 shrink-0 rounded text-fg-subtle group-hover:visible hover:bg-bg-sunken', menu ? 'visible' : 'invisible')} onClick={() => setMenu(true)}>
              <MoreHorizontal size={13} className="mx-auto" />
            </button>
            {menu && <Menu anchor={menuBtn.current} items={tidyItems} onClose={() => setMenu(false)} />}
          </>
        )}
        {canManage && !smart && (
          <button type="button" aria-label={`New board in ${board.name}`} className="invisible h-5 w-5 shrink-0 rounded text-fg-subtle group-hover:visible hover:bg-bg-sunken" onClick={() => ctx.onCreate(board)}>
            <Plus size={13} className="mx-auto" />
          </button>
        )}
      </div>
      {open && hasChildren && (
        <ul role="group" className="space-y-px">
          {children.data?.map((c) => (
            <TreeNode key={c.id} board={c} depth={depth + 1} ctx={ctx} />
          ))}
          {children.isPending && (
            <li className="py-1 text-xs text-fg-subtle" style={{ paddingLeft: 24 + depth * 14 }}>
              Loading…
            </li>
          )}
        </ul>
      )}
    </li>
  );
}
